import { useCallback, useMemo } from 'react';
import { getObjectId } from '@/helpers/object.id.helper';
import {
  buildFlowControlBrowsableSegmentIds,
  canStepBrowsableSegment,
  resolveAdjacentBrowsableSegmentId,
} from '@/helpers/flow-control-segment-navigation.helper';
import {
  findFlowSlotForControlExpandId,
  resolveFlowControlExpandIdFromCueRow,
  resolveFlowControlPlayingExpandIdSimulation,
} from '@/helpers/flow-control-playing-segment.helper';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useFlowControlMediaExpandStore } from '@/state/stores/use-flow-control-media-expand-store';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import { useGetSimulationCueSheetQuery } from '@/state/queries/player/use-get-simulation-cue-sheet';
import { useFlowControlPlayingSegmentExpandId } from './use-flow-control-playing-segment-expand-id';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';

export function useFlowControlSegmentBrowse() {
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const flow = useFlowStore((s) => s.flow);
  const flowToUse = flowPlan ?? flow;
  const planFlowId = flowToUse?.origin ? getObjectId(flowToUse.origin) : flowToUse?.id;
  const liveRunning = flowToUse?.liveContext?.isRunning ?? false;

  const expandId = useFlowControlMediaExpandStore((s) => s.expandId);
  const expandSource = useFlowControlMediaExpandStore((s) => s.expandSource);
  const setExpandId = useFlowControlMediaExpandStore((s) => s.setExpandId);
  const playingExpandId = useFlowControlPlayingSegmentExpandId();
  const simulationRows = useFlowControlSimulationCueStore((s) => s.rows);
  const simulationPointer = useFlowControlSimulationCueStore((s) => s.pointer);

  const { data: cueSheet } = useGetSimulationCueSheetQuery(planFlowId, {
    enabled: Boolean(planFlowId),
  });

  const cueSheetRows = cueSheet?.rows ?? simulationRows;
  const segmentIds = useMemo(
    () => buildFlowControlBrowsableSegmentIds(flowToUse, cueSheetRows),
    [cueSheetRows, flowToUse],
  );

  const activeBrowseId = expandSource === 'manual' && expandId != null ? expandId : (playingExpandId ?? expandId);
  const isBrowsing = expandSource === 'manual' && expandId != null && expandId !== playingExpandId;

  const stepSegment = useCallback(
    (delta: number) => {
      const nextId = resolveAdjacentBrowsableSegmentId(segmentIds, activeBrowseId, delta);
      if (!nextId) {
        return;
      }
      setExpandId(nextId, 'manual');
    },
    [activeBrowseId, segmentIds, setExpandId],
  );

  const resetToPlayingSegment = useCallback(
    (onSlotSelect?: (slot: FlowSlotDto, isUserInitiated?: boolean) => void) => {
      const targetExpandId =
        playingExpandId ??
        (!liveRunning ? resolveFlowControlExpandIdFromCueRow(simulationRows[simulationPointer]) : null);
      if (targetExpandId) {
        setExpandId(targetExpandId, 'playing');
        const slot = findFlowSlotForControlExpandId(flowPlan?.slots, targetExpandId);
        if (slot) {
          onSlotSelect?.(slot, false);
        }
        return;
      }
      setExpandId(null);
    },
    [flowPlan?.slots, liveRunning, playingExpandId, setExpandId, simulationPointer, simulationRows],
  );

  const snapToPlayingSegment = useCallback(() => {
    resetToPlayingSegment();
  }, [resetToPlayingSegment]);

  /** Sync expand store after simulation stepMedia updates the cue pointer. */
  const followPlayingSegmentAfterSimulationStep = useCallback(() => {
    const state = useFlowControlSimulationCueStore.getState();
    const nextPlayingExpandId = resolveFlowControlPlayingExpandIdSimulation(
      state.activeSegmentKind,
      state.activeSegmentSlotId,
      state.selectedItemIds,
      state.rows[state.pointer],
    );
    setExpandId(nextPlayingExpandId, 'playing');
  }, [setExpandId]);

  const canStepUp = canStepBrowsableSegment(segmentIds, activeBrowseId, -1);
  const canStepDown = canStepBrowsableSegment(segmentIds, activeBrowseId, 1);

  return {
    segmentIds,
    stepSegment,
    snapToPlayingSegment,
    resetToPlayingSegment,
    followPlayingSegmentAfterSimulationStep,
    isBrowsing,
    canStepUp,
    canStepDown,
  };
}
